<template>
  <div
    class="list-header usn pt order-none"
    :class="[
      { 'header-focused': listTitleFocused },
      { 'self-start': props.isCardsLayoutTimeline },
      { 'board-list-actions-dropdown-shown': actionsDropdownIsShown },
    ]"
    :style="props.headerFooterStyle"
    @mouseenter="headerMouseEnter"
    @mouseleave="headerMouseOut"
    @mousedown="isEditable"
  >
    <div
      v-tooltip="{
        content: numberOfCardsTooltipContent,
        placement: 'top-start',
        onShow: () => !!isInEpicBoard,
        offset: [0, -5],
      }"
      class="list-name-wrap"
    >
      <ToggleExpandButton
        v-if="props.expandable"
        class="w-5 h-5 p-0"
        :expanded="isExpanded"
        @update="onUpdateExpanded"
      />
      <StatusIconPicker
        v-if="props.showStatusIconPicker"
        v-tooltip="{
          content: translate('editStatus'),
          onShow: () => !props.disableStatusIconPicker,
          placement: 'top',
        }"
        :status-icon="listStatusIcon"
        :status-color="listStatusColor"
        :selected-status="{ id: list.behavior, color: listStatusColor }"
        :list-id="listIdRef"
        :disable-status-edit="props.disableStatusIconPicker"
        @statusItemClick="handleStatusItemClick"
        @colorSelect="handleColorSelect"
      />

      <UserAvatar
        v-if="groupByAssignee && !isUnassignedListId"
        class="mr-2 cursor-default"
        :member="list"
      />
      <ResourceIcon
        v-if="groupByEpic"
        :src="listStatusIcon"
        :color="listStatusColor"
        :type="list?.type || ''"
      />
      <div
        v-if="!listTitleFocused"
        v-tooltip="{
          content: translate('editName'),
          onShow: () => !props.disableStatusIconPicker,
          placement: 'top',
        }"
        class="list-title usn"
        :class="{ 'group-by': !groupByStatus || isSprint, 'safari-hidden': isSafari }"
        @click.exact="focusListTitle"
      >
        {{ list.title }}
      </div>
      <input
        v-if="listTitleFocused"
        ref="listTitleInput"
        v-model="listTitle"
        v-click-outside="handleInputBlur"
        type="text"
        class="list-title"
        contenteditable="true"
        @keyup.enter.stop.prevent="handleEnterKey($event)"
        @keydown.esc.stop="closeTitleEditInput"
        @mousedown="focusElementHandler"
        @mouseup="blurElementHandler"
      />
      <ButtonCustom
        v-if="listTitleFocused"
        id="discard-button"
        :type="'important-custom'"
        :variation="'extra-small'"
        class="pt usn group bg-important-bg hover:bg-important-hover border-important-border ml-2"
        :custom-icon-name="'x'"
        :no-label="true"
        @click="closeTitleEditInput"
      />
      <ButtonCustom
        v-if="listTitleFocused"
        :variation="'extra-small'"
        :type="'important-custom'"
        :custom-icon-name="'check'"
        class="pt usn second group bg-important-bg hover:bg-important-hover border-important-border"
        :no-label="true"
      />
      <span
        v-if="listCards && !listTitleFocused"
        v-tooltip="{
          content: translate('numberOfCards'),
          placement: 'top',
          onShow: () => !isInEpicBoard,
        }"
        class="cards-count usn"
      >
        {{ listCards.length }}
      </span>
      <span
        v-if="!listTitleFocused && totalEstimatePoints"
        v-tooltip="{
          content: totalEstimatePointsTooltipContent,
          placement: 'bottom',
          allowHTML: true,
        }"
        class="cards-count usn"
      >
        {{ totalEstimatePoints() }}
      </span>
    </div>
    <div
      v-if="showListActionsButton"
      class="board-list-actions-wrap pt"
    >
      <BoardListActions
        v-if="!listTitleFocused && !readOnly && !isRolledoverList"
        :list="list"
        @renameList="focusListTitle"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, toRef } from 'vue';
import ToggleExpandButton from '@/components/elements/common/ToggleExpandButton';
import StatusIconPicker from './StatusIconPicker';
import { groupByOption } from '@/types/groupByOption';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import useIsEditable from '@/utilities/composables/useIsEditable';
import { ResourceType } from '@/types/resources';
import useIsBrowser from '@/utilities/composables/useIsBrowser.ts';
import useTranslate from '@/utilities/composables/useTranslate';
import useElementFocus from '@/utilities/composables/useElementFocus.ts';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import BoardListActions from '@/components/widgets/BoardListActions';
import ButtonCustom from '@/components/elements/ButtonCustom';
import ResourceIcon from '@/components/elements/common/ResourceIcon';
import UserAvatar from '@/components/widgets/UserAvatar';
import useGetBoardList from '@/utilities/composables/useGetBoardList';
import { getCardStatus, getDefaultCardStatuses } from '@/utilities/defaultCardStatuses';
import cardStatusType from '@/types/cardStatusType';
import useCurrentRole from '@/utilities/composables/useCurrentRole';
import constants from '@/utilities/constants';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import isMobile from '@/utilities/isMobile';

const props = defineProps({
  listId: {
    type: [String, Number],
    required: true,
  },
  isCardsLayoutTimeline: {
    type: Boolean,
    required: true,
  },
  headerFooterStyle: {
    type: Object,
    required: true,
  },
  expandable: {
    type: Boolean,
    required: false,
    default: false,
  },
  showStatusIconPicker: {
    type: Boolean,
    required: false,
    default: false,
  },
  disableStatusIconPicker: {
    type: Boolean,
    required: false,
    default: false,
  },
  groupBy: {
    type: String,
    required: false,
    default: groupByOption.status,
  },
  listCards: {
    type: Array,
    required: true,
  },
  visible: {
    type: Boolean,
    required: false,
    default: true,
  },
  isEpicBoard: {
    type: Boolean,
    required: false,
    default: false,
  },
});
const store = useStore();
const route = useRoute();
const { embeddedBoardAttributes, embed, inEpicBoard } = useBoardEmbed();
const { isSafari } = useIsBrowser();
const { translate } = useTranslate();
const { focusElementHandler, blurElementHandler, isFocusElement } = useElementFocus();
const { iconPath, getCustomIconStyle } = useCustomIcon();
const { isEditable } = useIsEditable();

// Convert props to reactive refs
const groupByRef = toRef(props, 'groupBy');
const listIdRef = toRef(props, 'listId');

// Get reactive list from composable
const { list } = useGetBoardList(groupByRef, listIdRef);

const { isGuestReader } = useCurrentRole();
const emit = defineEmits(['headerMouseEnter', 'headerMouseOut']);
const listTitleFocused = ref(false);
const oldTitle = ref('');
const localTitle = ref('');
const debounceUpdateTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
const listTitleInput = ref<HTMLInputElement | null>(null);

const isExpanded = computed(() => {
  return store.getters.getListExpanded(currentBoardId.value, listIdRef.value);
});

const listTitle = computed({
  get() {
    return list.value.title || '';
  },
  set(title) {
    localTitle.value = title.trim();
  },
});

const showListActionsButton = computed(() => {
  return (
    props.visible &&
    (!props.expandable || (props.expandable && isExpanded.value) || actionsDropdownIsShown.value) &&
    !isMobile() &&
    !inEpicBoard &&
    !isGuestReader.value
  );
});
const isRolledoverList = computed(() => {
  return list.value?.orginalId === constants.rolledOverList;
});
const currentBoardId = computed(() => {
  if (embed) {
    return embeddedBoardAttributes?.value?.boardId;
  }
  return store.getters.currentBoardId;
});
const actionsDropdownIsShown = computed(() => {
  return store.getters.getCurrentListIdActionEdit === listIdRef.value;
});
const groupByAssignee = computed(() => {
  return groupByRef.value === groupByOption.assignee;
});
const groupByStatus = computed(() => {
  return groupByRef.value === groupByOption.status;
});
const groupByEpic = computed(() => {
  return groupByRef.value === groupByOption.epic;
});
const isUnassignedListId = computed(() => {
  return listIdRef.value === `${currentBoardId.value}-unassigned`;
});
const listStatusIcon = computed(() => {
  return (
    (getCardStatus(list.value.behavior, props.isEpicBoard) || {}).icon ||
    list.value.icon ||
    getCardStatus(cardStatusType.Backlog).icon ||
    ''
  );
});
const listStatusColor = computed(() => {
  const defaultCardStatuses = getDefaultCardStatuses();
  return (
    list.value.color ||
    (defaultCardStatuses[list.value.behavior] || {})?.color ||
    defaultCardStatuses.backlog.color ||
    ''
  );
});
const isSprint = computed(() => {
  return route.name === constants.routeNames.sprint || inboxSprint.value;
});
const inboxSprint = computed(() => {
  return (
    route.name === constants.routeNames.inbox &&
    store.getters.getCurrentInboxResource.resource_type === ResourceType.Sprint
  );
});
const readOnly = computed(() => {
  return embeddedBoardAttributes?.value?.readOnly ?? false;
});

const isInEpicBoard = computed(() => {
  return currentBoardId.value.includes(constants.inEpicBoardIdPrefix);
});

const numberOfCardsTooltipContent = computed(() => {
  const uniqueBoardIds = new Set(
    props.listCards.map((cardId: any) => store.getters.getCards[cardId]?.board_id)
  );
  const numberOfCards = props.listCards?.length;
  const cardTranslation = translate(numberOfCards === 1 ? 'card' : 'cards').toLowerCase();
  const boardsTranslation = translate('boardsCount', {
    count: uniqueBoardIds.size,
  }).toLowerCase();
  const fromPart = uniqueBoardIds.size >= 1 ? ` ${translate('from')} ${boardsTranslation}` : '';

  return `${numberOfCards} ${listTitle.value.toLowerCase()} ${cardTranslation}${fromPart}`;
});

function closeTitleEditInput($event: Event) {
  listTitleFocused.value = false;
  localTitle.value = oldTitle.value;
  updateListProperty('title', oldTitle.value);
  $event.stopPropagation();
  $event.preventDefault();
}
function focusListTitle() {
  if (!groupByStatus.value || readOnly.value || isSprint.value || inEpicBoard) return;
  listTitleFocused.value = true;
  oldTitle.value = list.value.title;
  nextTick(() => {
    if (listTitleInput.value) {
      listTitleInput.value.focus();
      listTitleInput.value.select();
    }
  });
}
function updateListProperty(propertyName: string, value: string) {
  const oldValue = list.value[propertyName];
  const updatedList = { [propertyName]: value };
  store.dispatch('setBoardListProp', { listId: listIdRef.value, propertyName, value });
  updateList(updatedList).catch(() => {
    store.dispatch('setBoardListProp', {
      listId: listIdRef.value,
      propertyName,
      value: oldValue,
    });
  });
}
function updateList(newList: any) {
  return store.dispatch('updateList', {
    boardId: currentBoardId.value,
    listId: listIdRef.value,
    list: newList,
  });
}
const headerMouseEnter = () => {
  emit('headerMouseEnter');
};
const headerMouseOut = () => {
  emit('headerMouseOut');
};
const onUpdateExpanded = (value: boolean) => {
  store.dispatch('setListsExpanded', {
    boardId: currentBoardId.value,
    listId: listIdRef.value,
    value,
  });
};
function handleStatusItemClick(status: any) {
  if (props.listCards.length > 0) {
    const resourceTypeTranslation = translate(props.isEpicBoard ? 'epics' : 'cards').toLowerCase();
    store.dispatch('addUiNotification', {
      message: translate('cannotModidyListStatus', {
        resourceType: resourceTypeTranslation,
      }),
      status: constants.uiNotificationStatuses.error,
      duration: 5000,
    });
    return;
  }
  const newList = {
    behavior: status.id,
    icon: status.icon,
    color: status.color,
  };
  store.dispatch('setBoardList', {
    listId: listIdRef.value,
    list: {
      ...list.value,
      ...newList,
      ...(localTitle.value && { title: localTitle.value }),
    },
  });
  updateList(newList);
  nextTick(() => {
    if (listTitleInput.value) {
      listTitleInput.value.focus();
    }
  });
}
function handleColorSelect(color: string) {
  const oldValue = list.value.color;
  const payload = { color };
  store.dispatch('setBoardListProp', {
    listId: listIdRef.value,
    propertyName: 'color',
    value: color,
  });
  // debounce color update
  if (debounceUpdateTimeout.value) {
    clearTimeout(debounceUpdateTimeout.value);
  }
  debounceUpdateTimeout.value = setTimeout(() => {
    updateList(payload).catch(() => {
      store.dispatch('setBoardListProp', {
        listId: list.value.id,
        propertyName: 'color',
        value: oldValue,
      });
    });
  }, 100);
}
function handleEnterKey(evt: KeyboardEvent) {
  handleInputBlur(evt as unknown as FocusEvent);
}

function handleInputBlur(evt: FocusEvent) {
  if (isFocusElement()) return;
  const classes = ['child-card-status-icon', 'status-icon-dropdown-wrap', 'status-icon-wrap'];
  const statusIconClick = classes.some((c) => (evt?.target as HTMLElement)?.className.includes(c));
  if (statusIconClick) {
    const { listId } = (evt.target as HTMLElement)?.dataset || {};
    if (listId === listIdRef.value) {
      return;
    }
  }
  if ((evt.relatedTarget as HTMLElement)?.id === 'discard-button') {
    listTitleFocused.value = false;
    localTitle.value = list.value.title;
    return;
  }
  if (!localTitle.value) {
    listTitleFocused.value = false;
    return;
  }
  updateListProperty('title', localTitle.value);
  listTitleFocused.value = false;
  localTitle.value = '';
}

const numberOfPointsLabel = (num: number) => {
  return num === 1 ? translate('onePoint') : translate('numberOfPoints', { num });
};
const totalEstimatePoints = () => {
  const currentProjectId = store.getters.getCurrentProjectId;
  const selectedEstimateSystem = store.getters.getEstimateSystemForProject(currentProjectId) || '';
  if (!selectedEstimateSystem) return '';
  const { values = [], type = '' } = constants.estimateSystems[selectedEstimateSystem] || {};
  if (type !== 'points' || !values.length) return '';
  const total = props.listCards.reduce((acc: number, cardId) => {
    const { estimate = 0 } = store.getters.getCards[cardId as string] || {};
    return estimate ? acc + values[estimate - 1] : acc;
  }, 0);
  return total ? `(${numberOfPointsLabel(Number(total))})` : '';
};
function totalEstimatePointsTooltipContent() {
  const iconStyle = getCustomIconStyle(iconPath('clock'));
  const icon = document.createElement('em');
  icon.classList.add('custom-icons', 'h-3', 'w-3', 'bg-attention-icon', 'mr-1');
  Object.assign(icon.style, iconStyle);
  const numOfCardsWithEstimate = props.listCards.reduce((acc: number, cardId) => {
    const { estimate = 0 } = store.getters.getCards[cardId as string] || {};
    return estimate ? acc + 1 : acc;
  }, 0);
  const text = translate('totalEstimatePointsTooltipContent', {
    totalPoints: totalEstimatePoints(),
    numOfCards: numOfCardsWithEstimate,
  });
  return `${icon.outerHTML} ${text} ${
    numOfCardsWithEstimate === 1
      ? translate('card').toLowerCase()
      : translate('cards').toLowerCase()
  }`;
}
</script>

<style lang="scss">
$columnLeftRightPadding: $boardColumnPaddingLeft + $boardColumnPaddingRight;
.list-header {
  width: calc(#{$columnWidth} + #{$columnLeftRightPadding});
  padding: 0px $boardColumnPaddingRight 0px $boardColumnPaddingLeft;
  display: flex;
  flex-shrink: 0;
  flex-direction: row;
  align-items: center;
  position: sticky;
  top: 0;
  order: 1;
  z-index: 3;
  box-sizing: border-box;
  transition: box-shadow 0.3s ease-in-out;
  max-height: $columnHeaderHeight;
  @apply bg-background-strongest;
  &.header-focused {
    .list-name-wrap {
      @apply bg-important-bg border-important-border border;
      border-radius: 8px;
      width: 100%;
      padding-right: 8px;
      box-shadow: 0px 0px 0px 1px inset $gray300;
      .second {
        margin-left: 4px;
      }
      &:hover {
        @apply bg-important-hover;
      }
      .list-title {
        max-width: 325px;
        &:focus {
          width: 100%;
        }
      }
    }
  }
  .list-name-wrap {
    display: flex;
    align-items: center;
    padding: 0px 8px 0px 4px;
    border-radius: 8px;
    min-width: 0;
    height: 42px;
    .status-icon-dropdown-wrap {
      background-color: transparent;
      border: unset;
      @apply rounded;
      &:hover:not(.disabled-edit) {
        @apply bg-hover-onHover;
      }
      .status-picker-dropdown {
        left: -4px;
        top: 42px;
      }
      .status-icon-wrap {
        border: unset;
      }
    }
    .list-title {
      font-weight: 500;
      font-size: 14px;
      max-width: 210px;
      line-height: 20px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 24px;
      width: 100%;
      outline: none;
      border: none;
      background: transparent;
      @apply text-text-default rounded py-0.5 px-1;
      &:focus {
        border: none;
        outline: none;
        cursor: text;
        width: auto;
      }
      span {
        white-space: nowrap;
      }
      &:hover:not(.group-by):not(:focus) {
        @apply bg-hover-onHover;
      }
    }
    .cards-count {
      font-weight: 500;
      font-size: 14px;
      line-height: 16px;
      letter-spacing: 0.1px;
      @apply text-text-placeholder whitespace-nowrap ml-1;
    }
  }
  .board-list-actions-wrap {
    @apply flex ml-auto;
    .board-list-actions {
      @apply hidden h-6 w-6 rounded;
    }
    &:hover {
      border-radius: 8px;
    }
  }
  &:hover,
  &.board-list-actions-dropdown-shown {
    .board-list-actions {
      @apply flex;
    }
  }
}
</style>
